import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useZodForm } from "@/lib/forms/useZodForm";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { z } from "zod";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Lock } from "lucide-react";
import { EquipmentLabel } from "@/components/exercises/EquipmentIcon";
import { ExperienceLevelLabel } from "@/components/exercises/ExperienceLevelBadge";
import {
  equipmentFilter,
  Exercise,
  experienceFilter,
  muscleGroups,
  trackingTypeFilter,
  typeFilter,
} from "./exerciseColumns";
import { errorToast } from "@/lib/utils/ToastUtils";
import { cn } from "@/lib/utils/ui";
import ReactPlayer from "react-player";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "sonner";
import DialogAlertBlocker from "../DialogAlertBlocker";
import { trpc } from "@/lib/trpc/client";
import { patchListCache, prependToList, replaceInList } from "@trainrr/api-client";
import { useHasPermission, useUserId } from "@/lib/states/session/hooks";
import {
  canEditExerciseDetails,
  canEditExerciseVideo,
  isSystemExercise,
} from "./exerciseAccess";

export default function ExerciseDialog({
  open,
  onOpenChange,
  exercise,
  onExerciseCreated,
}: {
  open: boolean;
  exercise?: Exercise;
  onOpenChange: (open: boolean) => void;
  onExerciseCreated?: (exercise: Exercise) => void;
}) {
  const [loading, setLoading] = React.useState(false);
  const userId = useUserId();
  const canManageLibrary = useHasPermission("exercises.manage_library");
  const systemExercise = Boolean(
    exercise && isSystemExercise(exercise),
  );
  const canEditDetails = canEditExerciseDetails(exercise, {
    userId,
    canManageLibrary,
  });
  const canEditVideo = canEditExerciseVideo(exercise, {
    userId,
    canManageLibrary,
  });
  const isReadOnly = Boolean(exercise) && !canEditDetails && !canEditVideo;
  const [alertBlockerOpen, setAlertBlockerOpen] = useState(false);

  const formSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    description: z.string().optional().default(""),
    tags: z.array(z.string()).default([]),
    muscleGroups: z.array(z.string().nullable()).default([null]),
    instructions: z.array(z.string()).default([""]),
    url: z.string().optional(),
    type: z.string().optional(),
    equipment: z.string().optional(),
    experience: z.string().optional(),
    trackingType: z.enum([
      "strength",
      "bodyweight",
      "timed",
      "distance_short",
      "distance_long",
    ]),
  });

  const defaultValues = useCallback(() => {
    return {
      name: exercise?.name ?? "",
      description: exercise?.description ?? "",
      tags: exercise?.tags ?? [],
      muscleGroups: exercise?.primary_muscles?.length
        ? exercise.primary_muscles
        : [null],
      instructions: exercise?.instructions?.length
        ? exercise.instructions
        : [""],
      url: exercise?.url ?? "",
      type: exercise?.type ?? "",
      equipment: exercise?.equipment ?? "",
      experience: exercise?.experience ?? "",
      trackingType:
        (exercise?.tracking_type as z.infer<
          typeof formSchema
        >["trackingType"]) ?? "strength",
    };
  }, [exercise]);

  const form = useZodForm(formSchema, {
    defaultValues: defaultValues(),
  });

  useEffect(() => {
    form.reset(defaultValues());
  }, [exercise, open]);

  const utils = trpc.useUtils();
  const finishSave = (saved: Exercise) => {
    onExerciseCreated?.(saved);
    toast.success(`Exercise ` + (exercise ? "updated" : "created"));
    setLoading(false);
    onOpenChange(false);
  };

  const createExercise = trpc.exercises.create.useMutation({
    onSuccess: (created) => {
      patchListCache(utils.exercises.list, (old) =>
        prependToList(old, created),
      );
      finishSave(created as Exercise);
    },
    onError: (error) => {
      console.error("Error saving exercise", error);
      errorToast(error);
      setLoading(false);
    },
  });
  const updateExercise = trpc.exercises.update.useMutation({
    onSuccess: (updated) => {
      patchListCache(utils.exercises.list, (old) =>
        replaceInList(old, updated),
      );
      finishSave(updated as Exercise);
    },
    onError: (error) => {
      console.error("Error saving exercise", error);
      errorToast(error);
      setLoading(false);
    },
  });
  const setVideo = trpc.exercises.setVideo.useMutation({
    onSuccess: (updated) => {
      patchListCache(utils.exercises.list, (old) =>
        replaceInList(old, updated),
      );
      toast.success("Exercise URL updated");
      setLoading(false);
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Error saving video", error);
      errorToast(error);
      setLoading(false);
    },
  });

  const handleOnOpenChange = (nextOpen: boolean) => {
    if (
      !isReadOnly &&
      form.formState.isDirty &&
      !form.formState.isSubmitSuccessful
    ) {
      setAlertBlockerOpen(true);
      return;
    }
    onOpenChange(nextOpen);
  };

  async function onSubmit(values: z.output<typeof formSchema>) {
    if (!canEditDetails && !canEditVideo) {
      return;
    }

    setLoading(true);

    const payload = {
      name: values.name,
      description: values.description,
      instructions: values.instructions.filter(
        (instruction) => instruction.trim() !== "",
      ),
      tags: values.tags ?? [],
      primary_muscles: values.muscleGroups.filter(
        (bp): bp is string => Boolean(bp) && bp !== "none",
      ),
      type: values.type || undefined,
      equipment: values.equipment || undefined,
      experience: values.experience || undefined,
      tracking_type: values.trackingType,
      url: values.url || undefined,
    };

    if (systemExercise) {
      if (
        canEditVideo &&
        values.url &&
        values.url !== exercise?.url &&
        exercise?.id
      ) {
        setVideo.mutate({ exerciseId: exercise.id, url: values.url });
        return;
      }
      setLoading(false);
      return;
    }

    if (!canEditDetails) {
      setLoading(false);
      return;
    }

    if (exercise?.id) {
      updateExercise.mutate({ id: exercise.id, ...payload });
      return;
    }

    createExercise.mutate(payload);
  }

  return (
    <>
      <DialogAlertBlocker
        shouldBlock={alertBlockerOpen}
        proceed={() => {
          setAlertBlockerOpen(false);
          onOpenChange(false);
        }}
        cancel={() => setAlertBlockerOpen(false)}
      />
      <Dialog
        open={open}
        onOpenChange={handleOnOpenChange}
        analyticsName="exercise_dialog"
      >
        <DialogContent
          className="max-w-[1200px] w-[95vw] max-h-[90vh] p-0 flex flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="h-full flex flex-col max-h-[90vh]"
            >
              <div className="flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">
                <div className="flex-1 p-6 overflow-y-auto">
                  <DialogTitle>
                    <h1 className="text-2xl font-semibold text-foreground mb-2">
                      {exercise ? exercise.name : "New Exercise"}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {exercise
                        ? canEditDetails
                          ? "Edit exercise details"
                          : canEditVideo
                            ? "You can add a video, but other fields are locked"
                            : "View exercise details"
                        : "Create a new exercise"}
                    </p>
                  </DialogTitle>

                  <div className="grid gap-6 py-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Exercise Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter exercise name"
                              type="text"
                              readOnly={!canEditDetails}
                              className="h-11"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="muscleGroups"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Target Muscles
                          </FormLabel>
                          <div className="space-y-3">
                            {field.value?.map((muscleGroup, index) => {
                              return (
                                <div
                                  key={index}
                                  className="flex items-center gap-3"
                                >
                                  <div className="flex-1">
                                    <MuscleGroupSelect
                                      muscleGroups={
                                        muscleGroups.map((mg) => mg.label) ?? []
                                      }
                                      muscleGroup={muscleGroup}
                                      systemExercise={!canEditDetails}
                                      onBodyPartChanged={(toBodyPart) => {
                                        const newBodyParts = [...field.value];
                                        newBodyParts[index] = toBodyPart;
                                        field.onChange(newBodyParts);
                                      }}
                                    />
                                  </div>
                                  {index > 0 && canEditDetails && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      type="button"
                                      onClick={() => {
                                        field.onChange(
                                          field.value?.filter(
                                            (_, i) => i !== index
                                          )
                                        );
                                      }}
                                      className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                            <Button
                              onClick={() => {
                                field.onChange([...(field.value ?? []), null]);
                              }}
                              type="button"
                              variant="outline"
                              size="sm"
                              className={cn(
                                "w-fit",
                                !canEditDetails && "hidden"
                              )}
                            >
                              + Add muscle group
                            </Button>
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                      <FormField
                        control={form.control}
                        name="equipment"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Equipment
                            </FormLabel>
                            <FormControl>
                              <Select
                                disabled={!canEditDetails}
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-11 disabled:opacity-100 disabled:cursor-auto [&>span]:inline-flex [&>span]:items-center">
                                  <SelectValue placeholder="Select equipment">
                                    {field.value ? (
                                      <EquipmentLabel
                                        equipment={field.value}
                                        label={
                                          equipmentFilter.find(
                                            (option) =>
                                              option.value === field.value,
                                          )?.label
                                        }
                                      />
                                    ) : undefined}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {equipmentFilter.map((type) => {
                                      return (
                                        <SelectItem
                                          key={type.value}
                                          value={type.value}
                                        >
                                          <EquipmentLabel
                                            equipment={type.value}
                                            label={type.label}
                                          />
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Exercise Type
                            </FormLabel>
                            <FormControl>
                              <Select
                                disabled={!canEditDetails}
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-11 disabled:opacity-100 disabled:cursor-auto">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {typeFilter.map((type) => {
                                      return (
                                        <SelectItem
                                          key={type.value}
                                          value={type.value}
                                        >
                                          {type.label}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="trackingType"
                        render={({ field }) => {
                          const selectedTrackingType = trackingTypeFilter.find(
                            (option) => option.value === field.value,
                          );
                          const TrackingIcon = selectedTrackingType?.Icon;

                          return (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Tracking Type
                            </FormLabel>
                            <FormControl>
                              <Select
                                disabled={!canEditDetails}
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-11 disabled:opacity-100 disabled:cursor-auto [&>span]:inline-flex [&>span]:items-center">
                                  <SelectValue placeholder="Select tracking type">
                                    {selectedTrackingType && TrackingIcon ? (
                                      <span className="inline-flex items-center gap-2">
                                        <TrackingIcon className="h-4 w-4 text-muted-foreground" />
                                        {selectedTrackingType.label}
                                      </span>
                                    ) : undefined}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {trackingTypeFilter.map((type) => {
                                      const Icon = type.Icon;
                                      return (
                                        <SelectItem
                                          key={type.value}
                                          value={type.value}
                                          indicatorPosition="right"
                                        >
                                          <div className="flex items-start gap-3 py-1">
                                            <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                            <div className="flex flex-col">
                                              <span className="text-sm">
                                                {type.label}
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                {type.subLabel}
                                              </span>
                                            </div>
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={form.control}
                        name="experience"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Experience Level
                            </FormLabel>
                            <FormControl>
                              <Select
                                disabled={!canEditDetails}
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="h-11 disabled:opacity-100 disabled:cursor-auto [&>span]:inline-flex [&>span]:items-center">
                                  <SelectValue placeholder="Select experience">
                                    {field.value ? (
                                      <ExperienceLevelLabel level={field.value} />
                                    ) : undefined}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {experienceFilter.map((type) => {
                                      return (
                                        <SelectItem
                                          key={type.value}
                                          value={type.value}
                                        >
                                          <ExperienceLevelLabel level={type.value} />
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="instructions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Instructions
                          </FormLabel>
                          {canEditDetails && (
                            <FormDescription>
                              Add step-by-step instructions for this exercise
                            </FormDescription>
                          )}
                          <FormControl>
                            <InstructionsList
                              instructions={field.value}
                              onChange={field.onChange}
                              readOnly={!canEditDetails}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Mobile YouTube field - hidden on large screens */}
                    <div className="lg:hidden">
                      <FormField
                        control={form.control}
                        name="url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              YouTube Link
                            </FormLabel>
                            <FormControl>
                              <div className="space-y-4">
                                <Input
                                  placeholder="https://youtube.com/watch?v=..."
                                  type="url"
                                  readOnly={!canEditVideo}
                                  className="h-11"
                                  {...field}
                                />
                                {field.value && (
                                  <div className="rounded-lg overflow-hidden border bg-background">
                                    <Player url={field.value} />
                                  </div>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex w-[400px] bg-muted/30 border-l flex-col min-h-0">
                  <div className="p-6 border-b bg-background/50 flex-shrink-0">
                    <DialogTitle>
                      <h2 className="text-lg font-semibold text-foreground">
                        Media
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {canEditVideo
                          ? "Add a video demonstration"
                          : "Video demonstration"}
                      </p>
                    </DialogTitle>
                  </div>

                  <div className="flex-1 p-6 space-y-4 overflow-y-auto">
                    <FormField
                      control={form.control}
                      name="url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            YouTube Link
                          </FormLabel>
                          <FormControl>
                            <div className="space-y-4">
                              <Input
                                placeholder="https://youtube.com/watch?v=..."
                                type="url"
                                readOnly={!canEditVideo}
                                className="h-11"
                                {...field}
                              />
                              {field.value && (
                                <div className="rounded-lg overflow-hidden border bg-background">
                                  <Player url={field.value} />
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="border-t bg-background/50 p-6 flex-shrink-0">
                <div className="flex items-center justify-between gap-4">
                  {(systemExercise || isReadOnly) && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Lock className="w-4 h-4 mr-2 shrink-0" />
                      {systemExercise
                        ? canEditVideo
                          ? "Some fields cannot be edited as this is a system exercise"
                          : "This is a Trainrr library exercise and can't be edited"
                        : "You can create your own exercises, but only the coach who created this one can edit it"}
                    </div>
                  )}
                  <div className="flex items-center gap-3 ml-auto">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleOnOpenChange(false)}
                      disabled={loading}
                    >
                      {isReadOnly ? "Close" : "Cancel"}
                    </Button>
                    {!isReadOnly && (
                      <Button
                        type="submit"
                        disabled={!form.formState.isDirty || loading}
                        className="min-w-[100px]"
                      >
                        {loading ? (
                          <>
                            <LoadingSpinner className="w-4 h-4 mr-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Exercise"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const MuscleGroupSelect = ({
  muscleGroups,
  muscleGroup,
  onBodyPartChanged,
  systemExercise,
}: {
  muscleGroups: string[];
  muscleGroup: string | null;
  onBodyPartChanged: (toBodyPart: string) => void;
  systemExercise: boolean;
}) => {
  return (
    <Select
      value={muscleGroup ?? "none"}
      onValueChange={onBodyPartChanged}
      disabled={systemExercise}
    >
      <SelectTrigger className="h-11 disabled:opacity-100 disabled:cursor-auto">
        <SelectValue placeholder="Select muscle group" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={"none"}>Select muscle group</SelectItem>
          {muscleGroups.map((muscleGroup) => {
            return (
              <SelectItem
                key={muscleGroup}
                value={muscleGroup.toLocaleLowerCase()}
              >
                {muscleGroup}
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

interface PlayerProps {
  url: string;
}

const Player: FunctionComponent<PlayerProps> = ({ url }) => {
  return (
    <div className="w-full aspect-video">
      <ReactPlayer
        url={url}
        width="100%"
        height="100%"
        muted={true}
        controls={true}
        config={{
          youtube: {
            playerVars: {
              modestbranding: 1,
              rel: 0,
            },
          },
        }}
      />
    </div>
  );
};

interface InstructionsListProps {
  instructions: string[];
  onChange: (instructions: string[]) => void;
  readOnly?: boolean;
}

const InstructionsList: FunctionComponent<InstructionsListProps> = ({
  instructions,
  onChange,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  const hasContent = instructions.some(
    (instruction) => instruction.trim() !== ""
  );
  const showCollapsed = !readOnly && !isExpanded && !hasContent;

  const updateInstruction = (index: number, value: string) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    onChange(newInstructions);
  };

  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleTextareaChange = (
    index: number,
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateInstruction(index, e.target.value);
    autoResize(e.target);
  };

  const addInstruction = (index: number) => {
    const newInstructions =
      instructions.length === 0 ? [""] : [...instructions];
    newInstructions.splice(index + 1, 0, "");
    onChange(newInstructions);
    setTimeout(() => {
      textareaRefs.current[index + 1]?.focus();
    }, 0);
  };

  // Auto-resize all textareas when instructions change
  useEffect(() => {
    textareaRefs.current.forEach((textarea) => {
      if (textarea) {
        autoResize(textarea);
      }
    });
  }, [instructions]);

  const removeInstruction = (index: number) => {
    if (instructions.length > 1) {
      const newInstructions = instructions.filter((_, i) => i !== index);
      onChange(newInstructions);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    const currentValue = instructions[index] ?? "";
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey || currentValue.trim() === "") {
        return;
      }
      const target = e.currentTarget;
      const isAtStart =
        target.selectionStart === 0 && target.selectionEnd === 0;
      addInstruction(isAtStart ? index - 1 : index);
      return;
    }

    if (e.key === "Backspace") {
      const target = e.currentTarget;
      const isAtStart = target.selectionStart === 0 && target.selectionEnd === 0;
      if (
        isAtStart &&
        currentValue.trim() === "" &&
        index > 0
      ) {
        e.preventDefault();
        removeInstruction(index);
        setTimeout(() => {
          const prevTextarea = textareaRefs.current[index - 1];
          if (prevTextarea) {
            prevTextarea.focus();
            prevTextarea.selectionStart = prevTextarea.value.length;
            prevTextarea.selectionEnd = prevTextarea.value.length;
          }
        }, 0);
      }
    }
  };

  // Ensure there's always at least one instruction
  const displayInstructions = instructions.length === 0 ? [""] : instructions;

  if (showCollapsed) {
    return (
      <Input
        placeholder="Add exercise instructions"
        className="h-11"
        onFocus={() => {
          setIsExpanded(true);
          if (instructions.length === 0) {
            onChange([""]);
          }
          setTimeout(() => {
            textareaRefs.current[0]?.focus();
          }, 0);
        }}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div className="rounded-lg border border-input bg-background px-4 py-3 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      {displayInstructions.map((instruction, index) => (
        <div key={index} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 w-6 self-start -mt-[1px] text-sm font-medium text-muted-foreground leading-6">
            {index + 1}.
          </div>
          <div className="flex-1">
            <Textarea
              ref={(el) => {
                textareaRefs.current[index] = el;
              }}
              value={instruction}
              onChange={(e) => handleTextareaChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              placeholder={
                index === 0
                  ? "Add first step for the exercise"
                  : `Step ${index + 1}...`
              }
              readOnly={readOnly}
              data-instruction-index={index}
              className="min-h-[28px] resize-none overflow-hidden border-0 bg-transparent px-0 py-0 leading-6 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              rows={1}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
